import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IRanking, IRankingApiProvider} from "data/providers/api/ranking.api.provider";
import {Bindings} from "data/constants/bindings";
import {LeaderboardType, RequestState} from "data/enums";

export interface IFilter {
	teamId: number;
}

const DEFAULT_FILTER: IFilter = {
	teamId: 0,
};

export interface IRankingStore {
	get list(): IRanking[];
	get isNextPage(): boolean;
	get selectedLeaderboard(): LeaderboardType;
	get isRequestSuccess(): boolean;
	get filter(): IFilter;

	resetPageCount(): void;
	fetchOverallLeaderboards(): Promise<void>;
	fetchFanLeaderboards(): Promise<void>;
	fetchPersonalLeaderboards(): Promise<void>;
	fetchMoreLeaderboards: () => void;
	setSelectedLeaderboard(type: LeaderboardType): void;
	setFilter(value: string, name: keyof IFilter): void;
	resetFilter(): void;
}

@injectable()
export class RankingStore implements IRankingStore {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable private _list: IRanking[] = [];
	@observable private _selectedLeaderboard: LeaderboardType = LeaderboardType.OVERALL;
	@observable private _nextPage: boolean = false;
	@observable private _filter: IFilter = DEFAULT_FILTER;
	private _page: number = 0;
	private _limit: number = 30;

	get list(): IRanking[] {
		return this._list;
	}

	get filter() {
		return this._filter;
	}

	get isRequestSuccess() {
		return this._requestState === RequestState.SUCCESS;
	}

	get isNextPage() {
		return this._nextPage;
	}

	get selectedLeaderboard() {
		return this._selectedLeaderboard;
	}
	constructor(@inject(Bindings.RankingApiProvider) private _rankingsApi: IRankingApiProvider) {
		makeAutoObservable(this);
	}

	@action resetPageCount() {
		this._page = 0;
	}

	@action setSelectedLeaderboard(type: LeaderboardType) {
		this._selectedLeaderboard = type;
	}

	@action setFilter(value: string, name: keyof IFilter) {
		this._filter[name] = Number(value);
	}

	@action resetFilter() {
		this._filter = DEFAULT_FILTER;
	}

	async fetchOverallLeaderboards() {
		this._requestState = RequestState.PENDING;
		const payload = {...this._filter, page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.overall(payload);

		runInAction(() => {
			this._list = success.rankings;
			this._nextPage = success.nextPage;
			this._requestState = RequestState.SUCCESS;
		});
	}

	async fetchFanLeaderboards() {
		this._requestState = RequestState.PENDING;
		const payload = {...this._filter, page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.team(payload);

		runInAction(() => {
			this._list = success.rankings;
			this._nextPage = success.nextPage;
			this._requestState = RequestState.SUCCESS;
		});
	}

	async fetchPersonalLeaderboards() {
		this._requestState = RequestState.PENDING;
		const payload = {...this._filter, page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.fan(payload);

		runInAction(() => {
			this._list = success.rankings;
			this._nextPage = success.nextPage;
			this._requestState = RequestState.SUCCESS;
		});
	}

	fetchMoreLeaderboards = () => {
		if (this._selectedLeaderboard === LeaderboardType.PERSONAL) {
			void this.fetchMorePersonalLeaderboards();
			return;
		}

		if (this._selectedLeaderboard === LeaderboardType.FAN) {
			void this.fetchMoreFanLeaderboards();
			return;
		}

		void this.fetchMoreOverallLeaderboards();
	};

	async fetchMorePersonalLeaderboards() {
		this._page += 1;
		const payload = {page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.fan(payload);

		runInAction(() => {
			this._list = [...this._list, ...success.rankings];
			this._nextPage = success.nextPage;
		});
	}

	async fetchMoreFanLeaderboards() {
		this._page += 1;
		const payload = {page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.team(payload);

		runInAction(() => {
			this._list = [...this._list, ...success.rankings];
			this._nextPage = success.nextPage;
		});
	}

	async fetchMoreOverallLeaderboards() {
		this._page += 1;
		const payload = {page: this._page, limit: this._limit};

		const {
			data: {success},
		} = await this._rankingsApi.overall(payload);

		runInAction(() => {
			this._list = [...this._list, ...success.rankings];
			this._nextPage = success.nextPage;
		});
	}
}
